// Strings for the public pages (payment link, billing invoice, subscription, refund claim, top-up,
// signup) and the redesigned receipt. Merged into strings.ts; every Slovak key is required by type.

export const enV2 = {
  "common.optional": "Optional",
  "common.loadingPage": "Loading…",
  "common.network": "Network",
  "common.show": "Show",
  "common.hide": "Hide",
  "common.secureNote": "Secure page. Payments go straight to {merchant}'s wallet — nobody else holds your money.",

  "error.linkTitle": "This link isn't valid",
  "error.linkBody": "It may be mistyped or no longer active. Ask the sender for a new link.",
  "error.rateTitle": "Too many requests",
  "error.rateBody": "Wait a minute, then try again.",

  "done.download": "Download receipt",
  "done.downloadHint": "Opens your browser's print dialog — choose “Save as PDF” to keep a copy.",
  "print.title": "Payment receipt",
  "print.issuedBy": "Issued through {operator}",
  "print.generated": "Generated {date}",

  "receipt.refunds": "Refunds",
  "receipt.refundedTotal": "{amount} {asset} refunded",
  "receipt.refundRequested": "Issued {date}",
  "receipt.refundConfirmed": "Confirmed {date}",

  "refundStatus.requested": "Requested",
  "refundStatus.awaiting_customer": "Waiting for address",
  "refundStatus.ready": "Being prepared",
  "refundStatus.submitted": "Sent, confirming",
  "refundStatus.confirmed": "Refunded",
  "refundStatus.failed": "Failed",
  "refundStatus.cancelled": "Cancelled",

  "link.title": "Pay {merchant}",
  "link.amount": "Amount",
  "link.enterAmount": "Enter the amount",
  "link.min": "Minimum {amount} {asset}",
  "link.max": "Maximum {amount} {asset}",
  "link.range": "Between {min} and {max} {asset}",
  "link.email": "E-mail for your receipt",
  "link.emailHint": "{merchant} receives this address together with your payment.",
  "link.continue": "Continue to payment",
  "link.creating": "Preparing your payment…",
  "link.payIn": "Paid in {asset} on {chain}",
  "link.inactiveTitle": "This payment link is no longer active",
  "link.inactiveBody": "The seller switched it off. Ask them for a new link.",
  "link.err.invalid_amount": "Enter a valid amount.",
  "link.err.too_precise": "Use at most {decimals} decimal places.",
  "link.err.below_min": "The minimum is {amount} {asset}.",
  "link.err.above_max": "The maximum is {amount} {asset}.",
  "link.err.email_required": "Enter your e-mail address.",
  "link.err.email_invalid": "This e-mail address doesn't look right.",
  "link.err.generic": "Couldn't start the payment: {message}",

  "billing.title": "Invoice {number}",
  "billing.from": "From",
  "billing.billTo": "Bill to",
  "billing.issued": "Issued",
  "billing.due": "Due",
  "billing.dueOnReceipt": "On receipt",
  "billing.item": "Description",
  "billing.qty": "Qty",
  "billing.unit": "Unit price",
  "billing.lineAmount": "Amount",
  "billing.subtotal": "Subtotal",
  "billing.totalDue": "Total due",
  "billing.total": "Total",
  "billing.notes": "Notes",
  "billing.pay": "Pay {amount} {asset}",
  "billing.opening": "Opening checkout…",
  "billing.pdf": "Download PDF",
  "billing.pdfNote": "The PDF is in English.",
  "billing.print": "Print",
  "billing.payIn": "Payable in {asset} on {chain}",
  "billing.paidOn": "Paid on {date}",
  "billing.viewReceipt": "View payment receipt",
  "billing.overdueNotice": "This invoice was due on {date}. You can still pay it.",
  "billing.voidTitle": "This invoice was voided",
  "billing.voidBody": "{merchant} cancelled it. There is nothing to pay.",
  "billing.paidTitle": "Paid — thank you",
  "billing.status.draft": "Draft",
  "billing.status.open": "Open",
  "billing.status.overdue": "Overdue",
  "billing.status.paid": "Paid",
  "billing.status.void": "Void",
  "billing.err.already_paid": "This invoice is already paid.",
  "billing.err.invoice_void": "This invoice was voided.",
  "billing.err.merchant_inactive": "{merchant} can't accept payments right now. Please contact them.",
  "billing.err.generic": "Couldn't open the checkout: {message}",
  "billing.company": "Company",

  "sub.title": "Your subscription",
  "sub.with": "with {merchant}",
  "sub.for": "For {name}",
  "sub.plan": "Plan",
  "sub.perWeek.one": "per week",
  "sub.perWeek.many": "every {n} weeks",
  "sub.perMonth.one": "per month",
  "sub.perMonth.many": "every {n} months",
  "sub.perYear.one": "per year",
  "sub.perYear.many": "every {n} years",
  "sub.next": "Next invoice",
  "sub.nextOn": "Issued on {date}",
  "sub.latest": "Latest invoice",
  "sub.openInvoice": "Open invoice",
  "sub.none": "No invoice has been issued yet.",
  "sub.dueOn": "due {date}",
  "sub.howTitle": "No automatic charges",
  "sub.howBody": "Each period {merchant} sends you an invoice and you pay it from your own wallet. Nothing is ever taken from your account.",
  "sub.status.active": "Active",
  "sub.status.paused": "Paused",
  "sub.status.cancelled": "Cancelled",
  "sub.pausedNotice": "{merchant} paused this subscription. No invoices are issued while it's paused.",
  "sub.cancel": "Cancel subscription",
  "sub.cancelTitle": "Cancel this subscription?",
  "sub.cancelBody": "{merchant} won't issue new invoices. Invoices already issued stay payable. You can't undo this here.",
  "sub.cancelConfirm": "Yes, cancel it",
  "sub.cancelKeep": "Keep subscription",
  "sub.cancelling": "Cancelling…",
  "sub.cancelledTitle": "Subscription cancelled",
  "sub.cancelledBody": "Cancelled on {date}. No new invoices will be issued.",
  "sub.cancelFailed": "Couldn't cancel: {message}",

  "refund.title": "Refund from {merchant}",
  "refund.amount": "Refund amount",
  "refund.reason": "Reason",
  "refund.formTitle": "Where should the refund go?",
  "refund.formIntro": "Enter the address of a {chain} wallet you control. {merchant} sends {asset} there from their own wallet.",
  "refund.address": "Your {chain} wallet address",
  "refund.addressConfirm": "Enter the same address again",
  "refund.addressConfirmHint": "A second entry rules out a typo.",
  "refund.exchangeTitle": "Don't use an exchange deposit address",
  "refund.exchangeBody": "Exchanges often credit only deposits they expect, so a refund sent to a deposit address can be lost. Use a wallet where you hold the keys, such as {wallets}.",
  "refund.selfCustody": "This is a wallet I control — not an exchange deposit address.",
  "refund.irreversible": "Blockchain transfers can't be reversed. Check the address carefully.",
  "refund.submit": "Confirm refund address",
  "refund.submitting": "Saving…",
  "refund.check.ok": "Valid {chain} address",
  "refund.check.match": "Addresses match",
  "refund.err.empty": "Enter an address.",
  "refund.err.format": "This isn't a valid {chain} address.",
  "refund.err.checksum": "The capital letters don't match this address's checksum. Copy it again from your wallet.",
  "refund.err.off_curve": "This is a token account or program address, not a wallet. Enter your wallet's main address.",
  "refund.err.zero": "This address can't receive funds.",
  "refund.err.mismatch": "The two addresses don't match.",
  "refund.err.terms": "Please confirm this is your own wallet.",
  "refund.warn.lowercase": "Tip: copy the address from your wallet so its built-in checksum can catch typos.",
  "refund.err.address_blocked": "This address can't receive a refund. Please contact {merchant}.",
  "refund.err.not_claimable": "The refund address is already set.",
  "refund.err.invalid_address": "{merchant} can't send a refund to this address: {message}",
  "refund.err.generic": "Couldn't save the address: {message}",
  "refund.step.issued": "Refund issued by {merchant}",
  "refund.step.address": "Your address confirmed",
  "refund.step.sent": "Sent on {chain}",
  "refund.step.confirmed": "Confirmed",
  "refund.readyTitle": "Address confirmed",
  "refund.readyBody": "{merchant} will send {amount} {asset} to this address from their wallet. This page updates on its own.",
  "refund.submittedTitle": "Refund on its way",
  "refund.submittedBody": "The transfer is being confirmed on {chain}.",
  "refund.confirmedTitle": "Refund completed",
  "refund.confirmedBody": "{amount} {asset} was sent to your wallet and confirmed on {chain}.",
  "refund.failedTitle": "The refund transfer didn't go through",
  "refund.failedBody": "{merchant} can send it again. Contact them if you have questions.",
  "refund.cancelledTitle": "Refund cancelled",
  "refund.cancelledBody": "{merchant} cancelled this refund. Contact them if you think this is a mistake.",
  "refund.destination": "Destination",
  "refund.issued": "Issued",

  "topup.title": "Fee credit top-up",
  "topup.for": "For {merchant}",
  "topup.credit": "Requested credit",
  "topup.auto": "Credit is added automatically when the transfer arrives. Send from any wallet or exchange — there is nothing to connect.",
  "topup.status.pending": "Awaiting transfer",
  "topup.status.confirming": "Confirming",
  "topup.status.credited": "Credited",
  "topup.status.held": "Held for review",
  "topup.status.expired": "Expired",
  "topup.creditedTitle": "Credit added",
  "topup.creditedBody": "{amount} {asset} was added to {merchant}'s fee credit.",
  "topup.heldTitle": "Transfer received — held for review",
  "topup.heldBody": "Crediting it would exceed the maximum fee-credit balance, so the operator reviews it first.",
  "topup.expiredTitle": "This top-up expired",
  "topup.expiredBody": "Create a new top-up in the merchant portal. A transfer that still arrives within 24 hours is matched automatically.",
  "topup.confirmingTitle": "Transfer seen — confirming",

  "signup.title": "Open a merchant account",
  "signup.with": "with {operator}",
  "signup.lead": "Accept USDC and USDT. Every payment goes straight to your own wallet.",
  "signup.leadApproval": "{operator} reviews new accounts before they can take payments.",
  "signup.sectionBusiness": "Business",
  "signup.sectionAccount": "Sign-in",
  "signup.sectionPayout": "Payouts",
  "signup.business": "Business name",
  "signup.email": "E-mail",
  "signup.password": "Password",
  "signup.passwordHint": "At least 12 characters. A short sentence is easy to remember and hard to guess.",
  "signup.strength": "Password strength: {label}",
  "signup.strength.0": "Empty",
  "signup.strength.1": "Too weak",
  "signup.strength.2": "Fair",
  "signup.strength.3": "Good",
  "signup.strength.4": "Strong",
  "signup.website": "Website",
  "signup.country": "Country",
  "signup.countryPick": "Select a country",
  "signup.volume": "Expected monthly volume",
  "signup.volumePick": "Select a range",
  "signup.volume.lt10k": "Under 10,000 USD",
  "signup.volume.10k-50k": "10,000 – 50,000 USD",
  "signup.volume.50k-250k": "50,000 – 250,000 USD",
  "signup.volume.250k-1m": "250,000 – 1,000,000 USD",
  "signup.volume.gt1m": "Over 1,000,000 USD",
  "signup.payoutHint": "Payments are sent straight to these wallets. Add at least one, and use wallets you control — not exchange deposit addresses.",
  "signup.solana": "Solana wallet address",
  "signup.base": "Base wallet address",
  "signup.referral": "Referral code",
  "signup.termsPre": "I agree to the",
  "signup.termsLink": "terms of service",
  "signup.and": "and",
  "signup.privacyLink": "privacy policy",
  "signup.confirmNoTerms": "I confirm the information above is correct.",
  "signup.submit": "Create account",
  "signup.submitting": "Creating account…",
  "signup.err.required": "Required.",
  "signup.err.email": "Enter a valid e-mail address.",
  "signup.err.passwordShort": "Use at least 12 characters.",
  "signup.err.passwordLong": "Use at most 256 characters.",
  "signup.err.passwordEmail": "The password can't be your e-mail address.",
  "signup.err.url": "Enter the full address, starting with https://",
  "signup.err.payout": "Add at least one payout address.",
  "signup.err.terms": "Please accept to continue.",
  "signup.err.referral": "Letters, digits, - and _ only.",
  "signup.err.email_taken": "An account with this e-mail already exists.",
  "signup.err.signup_disabled": "Sign-ups were just closed by the operator.",
  "signup.err.generic": "Couldn't create the account: {message}",
  "signup.closedTitle": "Sign-ups are closed",
  "signup.closedBody": "{operator} isn't accepting new merchant accounts online right now.",
  "signup.contact": "Contact {operator}",
  "signup.pendingTitle": "Application received",
  "signup.pendingBody": "Thanks, {name}. {operator} reviews every new merchant account.",
  "signup.approvedTitle": "Your account is ready",
  "signup.approvedBody": "Thanks, {name}. You can sign in to the merchant portal now.",
  "signup.next": "What happens next",
  "signup.nextVerify": "Open the verification link we sent to {email}.",
  "signup.nextReview": "{operator} reviews your application. Until then you can sign in, but not take payments.",
  "signup.nextPortal": "Sign in to the merchant portal with {email} and create your API key or first payment link.",
};

export type V2Key = keyof typeof enV2;

export const skV2: Record<V2Key, string> = {
  "common.optional": "Nepovinné",
  "common.loadingPage": "Načítavam…",
  "common.network": "Sieť",
  "common.show": "Zobraziť",
  "common.hide": "Skryť",
  "common.secureNote": "Zabezpečená stránka. Platby idú priamo do peňaženky {merchant} — nikto iný vaše peniaze nedrží.",

  "error.linkTitle": "Tento odkaz nie je platný",
  "error.linkBody": "Môže byť nesprávny alebo už nie je aktívny. Požiadajte odosielateľa o nový odkaz.",
  "error.rateTitle": "Príliš veľa požiadaviek",
  "error.rateBody": "Počkajte minútu a skúste to znova.",

  "done.download": "Stiahnuť potvrdenie",
  "done.downloadHint": "Otvorí sa dialóg tlače — zvoľte „Uložiť ako PDF“ a uschovajte si kópiu.",
  "print.title": "Potvrdenie o platbe",
  "print.issuedBy": "Vystavené cez {operator}",
  "print.generated": "Vytvorené {date}",

  "receipt.refunds": "Vrátenia peňazí",
  "receipt.refundedTotal": "Vrátené {amount} {asset}",
  "receipt.refundRequested": "Vystavené {date}",
  "receipt.refundConfirmed": "Potvrdené {date}",

  "refundStatus.requested": "Vyžiadané",
  "refundStatus.awaiting_customer": "Čaká na adresu",
  "refundStatus.ready": "Pripravuje sa",
  "refundStatus.submitted": "Odoslané, potvrdzuje sa",
  "refundStatus.confirmed": "Vrátené",
  "refundStatus.failed": "Zlyhalo",
  "refundStatus.cancelled": "Zrušené",

  "link.title": "Zaplatiť {merchant}",
  "link.amount": "Suma",
  "link.enterAmount": "Zadajte sumu",
  "link.min": "Minimum {amount} {asset}",
  "link.max": "Maximum {amount} {asset}",
  "link.range": "Od {min} do {max} {asset}",
  "link.email": "E-mail pre potvrdenie",
  "link.emailHint": "{merchant} dostane túto adresu spolu s vašou platbou.",
  "link.continue": "Pokračovať k platbe",
  "link.creating": "Pripravujem platbu…",
  "link.payIn": "Platba v {asset} v sieti {chain}",
  "link.inactiveTitle": "Tento platobný odkaz už nie je aktívny",
  "link.inactiveBody": "Predajca ho vypol. Požiadajte ho o nový odkaz.",
  "link.err.invalid_amount": "Zadajte platnú sumu.",
  "link.err.too_precise": "Použite najviac {decimals} desatinných miest.",
  "link.err.below_min": "Minimum je {amount} {asset}.",
  "link.err.above_max": "Maximum je {amount} {asset}.",
  "link.err.email_required": "Zadajte e-mailovú adresu.",
  "link.err.email_invalid": "Táto e-mailová adresa nevyzerá správne.",
  "link.err.generic": "Platbu sa nepodarilo spustiť: {message}",

  "billing.title": "Faktúra {number}",
  "billing.from": "Dodávateľ",
  "billing.billTo": "Odberateľ",
  "billing.issued": "Vystavená",
  "billing.due": "Splatnosť",
  "billing.dueOnReceipt": "Pri prijatí",
  "billing.item": "Popis",
  "billing.qty": "Množ.",
  "billing.unit": "Cena za kus",
  "billing.lineAmount": "Suma",
  "billing.subtotal": "Medzisúčet",
  "billing.totalDue": "Na úhradu",
  "billing.total": "Spolu",
  "billing.notes": "Poznámka",
  "billing.pay": "Zaplatiť {amount} {asset}",
  "billing.opening": "Otváram platbu…",
  "billing.pdf": "Stiahnuť PDF",
  "billing.pdfNote": "PDF je v angličtine.",
  "billing.print": "Tlačiť",
  "billing.payIn": "Splatné v {asset} v sieti {chain}",
  "billing.paidOn": "Zaplatené {date}",
  "billing.viewReceipt": "Zobraziť potvrdenie o platbe",
  "billing.overdueNotice": "Faktúra bola splatná {date}. Stále ju môžete zaplatiť.",
  "billing.voidTitle": "Táto faktúra bola stornovaná",
  "billing.voidBody": "{merchant} ju zrušil. Nie je čo platiť.",
  "billing.paidTitle": "Zaplatené — ďakujeme",
  "billing.status.draft": "Koncept",
  "billing.status.open": "Otvorená",
  "billing.status.overdue": "Po splatnosti",
  "billing.status.paid": "Zaplatená",
  "billing.status.void": "Stornovaná",
  "billing.err.already_paid": "Táto faktúra je už zaplatená.",
  "billing.err.invoice_void": "Táto faktúra bola stornovaná.",
  "billing.err.merchant_inactive": "{merchant} teraz nemôže prijímať platby. Kontaktujte ho prosím.",
  "billing.err.generic": "Platbu sa nepodarilo otvoriť: {message}",
  "billing.company": "Firma",

  "sub.title": "Vaše predplatné",
  "sub.with": "u {merchant}",
  "sub.for": "Pre {name}",
  "sub.plan": "Plán",
  "sub.perWeek.one": "týždenne",
  "sub.perWeek.many": "každých {n} týždňov",
  "sub.perMonth.one": "mesačne",
  "sub.perMonth.many": "každých {n} mesiacov",
  "sub.perYear.one": "ročne",
  "sub.perYear.many": "každých {n} rokov",
  "sub.next": "Ďalšia faktúra",
  "sub.nextOn": "Vystaví sa {date}",
  "sub.latest": "Posledná faktúra",
  "sub.openInvoice": "Otvoriť faktúru",
  "sub.none": "Zatiaľ nebola vystavená žiadna faktúra.",
  "sub.dueOn": "splatná {date}",
  "sub.howTitle": "Žiadne automatické strhávanie",
  "sub.howBody": "Každé obdobie vám {merchant} pošle faktúru a zaplatíte ju zo svojej peňaženky. Z vášho účtu sa nikdy nič nestrhne.",
  "sub.status.active": "Aktívne",
  "sub.status.paused": "Pozastavené",
  "sub.status.cancelled": "Zrušené",
  "sub.pausedNotice": "{merchant} toto predplatné pozastavil. Kým je pozastavené, faktúry sa nevystavujú.",
  "sub.cancel": "Zrušiť predplatné",
  "sub.cancelTitle": "Zrušiť toto predplatné?",
  "sub.cancelBody": "{merchant} už nebude vystavovať nové faktúry. Už vystavené faktúry zostávajú splatné. Tu to nemožno vrátiť späť.",
  "sub.cancelConfirm": "Áno, zrušiť",
  "sub.cancelKeep": "Ponechať predplatné",
  "sub.cancelling": "Ruším…",
  "sub.cancelledTitle": "Predplatné zrušené",
  "sub.cancelledBody": "Zrušené {date}. Nové faktúry sa už nebudú vystavovať.",
  "sub.cancelFailed": "Zrušenie zlyhalo: {message}",

  "refund.title": "Vrátenie peňazí od {merchant}",
  "refund.amount": "Vrátená suma",
  "refund.reason": "Dôvod",
  "refund.formTitle": "Kam poslať vrátené peniaze?",
  "refund.formIntro": "Zadajte adresu peňaženky v sieti {chain}, ktorú ovládate. {merchant} tam pošle {asset} zo svojej peňaženky.",
  "refund.address": "Adresa vašej peňaženky v sieti {chain}",
  "refund.addressConfirm": "Zadajte tú istú adresu znova",
  "refund.addressConfirmHint": "Druhé zadanie vylúči preklep.",
  "refund.exchangeTitle": "Nepoužívajte vkladovú adresu burzy",
  "refund.exchangeBody": "Burzy často pripíšu len vklady, ktoré očakávajú, takže vrátenie na vkladovú adresu sa môže stratiť. Použite peňaženku, ku ktorej máte kľúče, napríklad {wallets}.",
  "refund.selfCustody": "Toto je peňaženka, ktorú ovládam — nie vkladová adresa burzy.",
  "refund.irreversible": "Prevody na blockchaine sa nedajú vrátiť. Adresu starostlivo skontrolujte.",
  "refund.submit": "Potvrdiť adresu",
  "refund.submitting": "Ukladám…",
  "refund.check.ok": "Platná adresa v sieti {chain}",
  "refund.check.match": "Adresy sa zhodujú",
  "refund.err.empty": "Zadajte adresu.",
  "refund.err.format": "Toto nie je platná adresa v sieti {chain}.",
  "refund.err.checksum": "Veľké a malé písmená nezodpovedajú kontrolnému súčtu adresy. Skopírujte ju znova z peňaženky.",
  "refund.err.off_curve": "Toto je tokenový účet alebo adresa programu, nie peňaženka. Zadajte hlavnú adresu svojej peňaženky.",
  "refund.err.zero": "Na túto adresu nemožno poslať prostriedky.",
  "refund.err.mismatch": "Adresy sa nezhodujú.",
  "refund.err.terms": "Potvrďte prosím, že ide o vašu vlastnú peňaženku.",
  "refund.warn.lowercase": "Tip: skopírujte adresu priamo z peňaženky, jej kontrolný súčet potom zachytí preklepy.",
  "refund.err.address_blocked": "Na túto adresu nemožno vrátiť peniaze. Kontaktujte prosím {merchant}.",
  "refund.err.not_claimable": "Adresa pre vrátenie je už nastavená.",
  "refund.err.invalid_address": "{merchant} nemôže poslať peniaze na túto adresu: {message}",
  "refund.err.generic": "Adresu sa nepodarilo uložiť: {message}",
  "refund.step.issued": "{merchant} vystavil vrátenie",
  "refund.step.address": "Vaša adresa potvrdená",
  "refund.step.sent": "Odoslané v sieti {chain}",
  "refund.step.confirmed": "Potvrdené",
  "refund.readyTitle": "Adresa potvrdená",
  "refund.readyBody": "{merchant} pošle {amount} {asset} na túto adresu zo svojej peňaženky. Stránka sa aktualizuje sama.",
  "refund.submittedTitle": "Vrátenie je na ceste",
  "refund.submittedBody": "Prevod sa potvrdzuje v sieti {chain}.",
  "refund.confirmedTitle": "Peniaze vrátené",
  "refund.confirmedBody": "{amount} {asset} bolo poslaných do vašej peňaženky a potvrdených v sieti {chain}.",
  "refund.failedTitle": "Prevod vrátenia neprešiel",
  "refund.failedBody": "{merchant} ho môže poslať znova. Ak máte otázky, kontaktujte ho.",
  "refund.cancelledTitle": "Vrátenie zrušené",
  "refund.cancelledBody": "{merchant} toto vrátenie zrušil. Ak si myslíte, že ide o omyl, kontaktujte ho.",
  "refund.destination": "Cieľová adresa",
  "refund.issued": "Vystavené",

  "topup.title": "Dobitie kreditu na poplatky",
  "topup.for": "Pre {merchant}",
  "topup.credit": "Požadovaný kredit",
  "topup.auto": "Kredit sa pripíše automaticky, keď prevod dorazí. Pošlite ho z ľubovoľnej peňaženky alebo burzy — nič netreba pripájať.",
  "topup.status.pending": "Čaká na prevod",
  "topup.status.confirming": "Potvrdzuje sa",
  "topup.status.credited": "Pripísané",
  "topup.status.held": "Čaká na kontrolu",
  "topup.status.expired": "Vypršalo",
  "topup.creditedTitle": "Kredit pripísaný",
  "topup.creditedBody": "Na kredit {merchant} sa pripísalo {amount} {asset}.",
  "topup.heldTitle": "Prevod prijatý — čaká na kontrolu",
  "topup.heldBody": "Pripísaním by sa prekročil maximálny zostatok kreditu, preto ho najprv skontroluje prevádzkovateľ.",
  "topup.expiredTitle": "Toto dobitie vypršalo",
  "topup.expiredBody": "Vytvorte nové dobitie v portáli obchodníka. Prevod, ktorý dorazí do 24 hodín, sa priradí automaticky.",
  "topup.confirmingTitle": "Prevod zachytený — potvrdzuje sa",

  "signup.title": "Založte si účet obchodníka",
  "signup.with": "u {operator}",
  "signup.lead": "Prijímajte USDC a USDT. Každá platba ide priamo do vašej vlastnej peňaženky.",
  "signup.leadApproval": "{operator} nové účty pred spustením platieb schvaľuje.",
  "signup.sectionBusiness": "Firma",
  "signup.sectionAccount": "Prihlásenie",
  "signup.sectionPayout": "Výplaty",
  "signup.business": "Názov firmy",
  "signup.email": "E-mail",
  "signup.password": "Heslo",
  "signup.passwordHint": "Aspoň 12 znakov. Krátka veta sa ľahko pamätá a ťažko háda.",
  "signup.strength": "Sila hesla: {label}",
  "signup.strength.0": "Prázdne",
  "signup.strength.1": "Príliš slabé",
  "signup.strength.2": "Priemerné",
  "signup.strength.3": "Dobré",
  "signup.strength.4": "Silné",
  "signup.website": "Web",
  "signup.country": "Krajina",
  "signup.countryPick": "Vyberte krajinu",
  "signup.volume": "Očakávaný mesačný objem",
  "signup.volumePick": "Vyberte rozsah",
  "signup.volume.lt10k": "Do 10 000 USD",
  "signup.volume.10k-50k": "10 000 – 50 000 USD",
  "signup.volume.50k-250k": "50 000 – 250 000 USD",
  "signup.volume.250k-1m": "250 000 – 1 000 000 USD",
  "signup.volume.gt1m": "Nad 1 000 000 USD",
  "signup.payoutHint": "Platby idú priamo do týchto peňaženiek. Pridajte aspoň jednu a použite peňaženky, ktoré ovládate — nie vkladové adresy búrz.",
  "signup.solana": "Adresa peňaženky Solana",
  "signup.base": "Adresa peňaženky Base",
  "signup.referral": "Odporúčací kód",
  "signup.termsPre": "Súhlasím s",
  "signup.termsLink": "obchodnými podmienkami",
  "signup.and": "a",
  "signup.privacyLink": "zásadami ochrany súkromia",
  "signup.confirmNoTerms": "Potvrdzujem, že údaje vyššie sú správne.",
  "signup.submit": "Vytvoriť účet",
  "signup.submitting": "Vytváram účet…",
  "signup.err.required": "Povinné.",
  "signup.err.email": "Zadajte platnú e-mailovú adresu.",
  "signup.err.passwordShort": "Použite aspoň 12 znakov.",
  "signup.err.passwordLong": "Použite najviac 256 znakov.",
  "signup.err.passwordEmail": "Heslo nemôže byť vaša e-mailová adresa.",
  "signup.err.url": "Zadajte celú adresu začínajúcu https://",
  "signup.err.payout": "Pridajte aspoň jednu adresu na výplaty.",
  "signup.err.terms": "Pre pokračovanie prosím potvrďte súhlas.",
  "signup.err.referral": "Len písmená, číslice, - a _.",
  "signup.err.email_taken": "Účet s týmto e-mailom už existuje.",
  "signup.err.signup_disabled": "Prevádzkovateľ práve uzavrel registrácie.",
  "signup.err.generic": "Účet sa nepodarilo vytvoriť: {message}",
  "signup.closedTitle": "Registrácie sú uzavreté",
  "signup.closedBody": "{operator} momentálne neprijíma nových obchodníkov online.",
  "signup.contact": "Kontaktovať {operator}",
  "signup.pendingTitle": "Žiadosť prijatá",
  "signup.pendingBody": "Ďakujeme, {name}. {operator} kontroluje každý nový účet obchodníka.",
  "signup.approvedTitle": "Váš účet je pripravený",
  "signup.approvedBody": "Ďakujeme, {name}. Do portálu obchodníka sa môžete prihlásiť hneď.",
  "signup.next": "Čo bude ďalej",
  "signup.nextVerify": "Otvorte overovací odkaz, ktorý sme poslali na {email}.",
  "signup.nextReview": "{operator} skontroluje vašu žiadosť. Dovtedy sa môžete prihlásiť, ale nie prijímať platby.",
  "signup.nextPortal": "Prihláste sa do portálu obchodníka s {email} a vytvorte si API kľúč alebo prvý platobný odkaz.",
};
